/**
 * Document Processor Service
 * Handles conversion of various document formats to markdown
 * Implements size limits and processing strategies
 */

import mammoth from 'mammoth';
import logger from '../utils/logger';
import { 
  DOCUMENT_LIMITS, 
  estimateTokens, 
  formatBytes, 
  estimateReadingTime,
  getProcessingStrategy,
  type ProcessingStrategy 
} from '../constants/documentLimits';

export interface DocumentSection {
  id: string;
  title: string;
  content: string;
  level: number;
  preview: string;
  tokens: number;
}

export interface ProcessedDocument {
  content: string;              // Full markdown content
  displayContent: string;       // Initial display content
  sections: DocumentSection[];  // Extracted sections
  metadata: {
    originalFileName: string;
    originalSize: number;
    markdownSize: number;
    tokens: number;
    processingStrategy: ProcessingStrategy;
    readingTime: number;
    sectionCount: number;
  };
}

export interface ConversionResult {
  markdown: string;
  warnings: string[];
}

class DocumentProcessorService {
  /**
   * Validate file size before processing
   */
  validateFileSize(file: File): { valid: boolean; error?: string; warning?: string } {
    if (file.size > DOCUMENT_LIMITS.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File too large (${formatBytes(file.size)}). Maximum allowed: ${formatBytes(DOCUMENT_LIMITS.MAX_FILE_SIZE)}. Please split into smaller documents.`
      };
    }
    
    if (file.size > DOCUMENT_LIMITS.WARN_FILE_SIZE) {
      return {
        valid: true,
        warning: `Large file detected (${formatBytes(file.size)}). Processing may take 15-30 seconds.`
      };
    }
    
    return { valid: true };
  }

  /**
   * Convert file to markdown based on type
   */
  async convertToMarkdown(file: File): Promise<ConversionResult> {
    const warnings: string[] = [];
    
    try {
      // Text files - direct read
      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const text = await file.text();
        return { markdown: text, warnings };
      }
      
      // Markdown files - direct read
      if (file.type === 'text/markdown' || file.name.endsWith('.md')) {
        const text = await file.text();
        return { markdown: text, warnings };
      }
      
      // Word documents (.docx)
      if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.endsWith('.docx')
      ) {
        return await this.convertDocxToMarkdown(file);
      }
      
      // HTML files
      if (file.type === 'text/html' || file.name.endsWith('.html')) {
        const html = await file.text();
        const markdown = this.htmlToMarkdown(html);
        return { markdown, warnings };
      }
      
      // Unsupported format
      throw new Error(`Unsupported file format: ${file.type || file.name}`);
      
    } catch (error) {
      logger.error('[DocumentProcessor] Conversion error:', error);
      throw new Error(`Failed to convert document: ${(error as Error).message}`);
    }
  }

  /**
   * Convert .docx to markdown using mammoth
   */
  private async convertDocxToMarkdown(file: File): Promise<ConversionResult> {
    const warnings: string[] = [];
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "p[style-name='Heading 1'] => h1",
            "p[style-name='Heading 2'] => h2",
            "p[style-name='Heading 3'] => h3",
            "p[style-name='Heading 4'] => h4",
          ]
        }
      );
      
      if (result.messages.length > 0) {
        warnings.push(...result.messages.map((m: any) => m.message));
      }
      
      // Convert HTML to markdown
      const markdown = this.htmlToMarkdown(result.value);
      
      return {
        markdown,
        warnings
      };
    } catch (error) {
      throw new Error(`Failed to convert .docx file: ${(error as Error).message}`);
    }
  }

  /**
   * Basic HTML to Markdown conversion
   */
  private htmlToMarkdown(html: string): string {
    // Remove scripts and styles
    let markdown = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
    
    // Convert headings
    markdown = markdown
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
      .replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n')
      .replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1\n\n')
      .replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1\n\n');
    
    // Convert links and images
    markdown = markdown
      .replace(/<a\s+href="([^"]+)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
      .replace(/<img\s+src="([^"]+)"[^>]*alt="([^"]*)"[^>]*>/gi, '![$2]($1)');
    
    // Convert lists
    markdown = markdown
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
      .replace(/<\/?[ou]l[^>]*>/gi, '\n');
    
    // Convert paragraphs and line breaks
    markdown = markdown
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
      .replace(/<br\s*\/?>/gi, '\n');
    
    // Convert bold and italic
    markdown = markdown
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');
    
    // Remove remaining HTML tags
    markdown = markdown.replace(/<[^>]+>/g, '');
    
    // Decode HTML entities
    markdown = markdown
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    // Clean up whitespace
    markdown = markdown
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    return markdown;
  }

  /**
   * Extract sections from markdown based on headers
   */
  extractSections(markdown: string): DocumentSection[] {
    const sections: DocumentSection[] = [];
    const lines = markdown.split('\n');
    
    let currentSection: DocumentSection | null = null;
    let currentContent: string[] = [];
    let sectionIndex = 0;
    
    for (const line of lines) {
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      
      if (headerMatch) {
        // Save previous section
        if (currentSection) {
          currentSection.content = currentContent.join('\n').trim();
          currentSection.preview = this.createPreview(currentSection.content);
          currentSection.tokens = estimateTokens(currentSection.content);
          sections.push(currentSection);
        }
        
        // Start new section
        const level = headerMatch[1].length;
        const title = headerMatch[2].trim();
        
        currentSection = {
          id: `section-${sectionIndex++}`,
          title,
          level,
          content: '',
          preview: '',
          tokens: 0
        };
        currentContent = [];
      } else if (currentSection) {
        currentContent.push(line);
      }
    }
    
    // Save last section
    if (currentSection) {
      currentSection.content = currentContent.join('\n').trim();
      currentSection.preview = this.createPreview(currentSection.content);
      currentSection.tokens = estimateTokens(currentSection.content);
      sections.push(currentSection);
    }
    
    // Limit to MAX_SECTIONS
    return sections.slice(0, DOCUMENT_LIMITS.MAX_SECTIONS);
  }

  /**
   * Create preview text from content
   */
  private createPreview(content: string): string {
    // Remove markdown formatting for preview
    const plainText = content
      .replace(/[#*`_\[\]()]/g, '')
      .replace(/\n+/g, ' ')
      .trim();
    
    if (plainText.length <= DOCUMENT_LIMITS.SECTION_PREVIEW_LENGTH) {
      return plainText;
    }
    
    return plainText.substring(0, DOCUMENT_LIMITS.SECTION_PREVIEW_LENGTH) + '...';
  }

  /**
   * Process document with strategy
   */
  async processDocument(file: File): Promise<ProcessedDocument> {
    // Validate size
    const validation = this.validateFileSize(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }
    
    // Convert to markdown with timeout
    const conversionPromise = this.convertToMarkdown(file);
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Conversion timeout exceeded')), DOCUMENT_LIMITS.CONVERSION_TIMEOUT)
    );
    
    const { markdown, warnings } = await Promise.race([conversionPromise, timeoutPromise]);
    
    if (warnings.length > 0) {
      logger.warn('[DocumentProcessor] Conversion warnings:', warnings);
    }
    
    // Validate markdown size
    if (markdown.length > DOCUMENT_LIMITS.MAX_MARKDOWN_SIZE) {
      throw new Error(
        `Converted document too large (${formatBytes(markdown.length)}). ` +
        `Maximum allowed: ${formatBytes(DOCUMENT_LIMITS.MAX_MARKDOWN_SIZE)}. ` +
        `Please split into smaller documents.`
      );
    }
    
    // Estimate tokens
    const tokens = estimateTokens(markdown);
    if (tokens > DOCUMENT_LIMITS.MAX_TOKENS) {
      throw new Error(
        `Document exceeds token limit (${tokens.toLocaleString()} tokens). ` +
        `Maximum: ${DOCUMENT_LIMITS.MAX_TOKENS.toLocaleString()} tokens.`
      );
    }
    
    // Determine processing strategy
    const strategy = getProcessingStrategy(tokens);
    
    // Extract sections
    const sections = this.extractSections(markdown);
    
    // Create display content based on strategy
    let displayContent: string;
    
    switch (strategy) {
      case 'FULL_DISPLAY':
        displayContent = markdown;
        break;
        
      case 'CHUNKED':
      case 'COLLAPSED_SECTIONS':
        // Show first few sections
        displayContent = sections
          .slice(0, DOCUMENT_LIMITS.INITIAL_DISPLAY_SECTIONS)
          .map(s => `${'#'.repeat(s.level)} ${s.title}\n\n${s.content}`)
          .join('\n\n');
        break;
        
      case 'SUMMARY_ONLY':
        // Show only section titles as TOC
        displayContent = '# Table of Contents\n\n' +
          sections.map(s => `${'  '.repeat(s.level - 1)}- ${s.title}`).join('\n');
        break;
    }
    
    return {
      content: markdown,
      displayContent,
      sections,
      metadata: {
        originalFileName: file.name,
        originalSize: file.size,
        markdownSize: markdown.length,
        tokens,
        processingStrategy: strategy,
        readingTime: estimateReadingTime(markdown),
        sectionCount: sections.length
      }
    };
  }
}

export const documentProcessorService = new DocumentProcessorService();
export default documentProcessorService;
